/**
 * patientForms.service.ts
 *
 * API client for the consolidated patient "Forms" tab.
 * Mirrors legacy patient-survey-list.php / forms_survey_body.php behavior,
 * scoped to surveys_responses + patients_form_submissions
 * (Demographics / History / Financing only).
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import { COMPLETED_FORM_PDF_REQUEST, toCompletedFormPdf, type CompletedFormPdf } from './completedFormPdf';

export type FormListType = 'formSV' | 'formDM' | 'formHT' | 'formFN';
export type FormListSource = 'survey_response' | 'submission';

export interface FormListRow {
  id: number;
  source: FormListSource;
  type: FormListType;
  title: string;
  signed: boolean;
  signatureLabel: string;
  date: string | null;
  surveysId?: number;
  formType?: 'Demographics' | 'History' | 'Financing';
  wasRead?: boolean;
  deleted?: boolean;
  isGeneralIntake?: boolean;
  generalIntakeStatus?: 'current' | 'old' | null;
}

export interface SurveyResponseOption {
  id: number;
  text: string;
  preselect: string;
}

export interface SurveyResponseItem {
  orderId: number;
  itemId: number;
  active: boolean;
  questionType: number;
  customQuestionType: number;
  questionContent: string;
  instructionText: string;
  groupId: number;
  groupTitle: string;
  options: SurveyResponseOption[];
  /** Which table the option ids belong to: shared list, per-question list, or '' (not a choice item). */
  optionSource?: 'shared' | 'perQuestion' | '';
  value: string | null;
  /** Review (type 6): approved for public posting. */
  approved?: boolean;
  addOther?: boolean;
}

export interface SurveyResponseDetail {
  survey: {
    id: number;
    title: string;
    color1: string;
    color2: string;
    headerMessage: string;
    contactMe: string;
    accountId: number;
  };
  response: {
    id: number;
    patientId: number;
    planId: number;
    dateCreated: string | null;
    dateCompleted: string | null;
    endDate: string | null;
    wasRead: boolean;
    contactMe: string;
    contactType: string;
    phoneNumber: string;
    /** False when the submission has no saved copy of the form it was filled in against, so today's wording is shown. */
    versioned?: boolean;
  };
  patient: {
    firstName: string;
    lastName: string;
    postName: string;
  } | null;
  physician: { name: string };
  facility: { name: string; address: string; phone: string };
  items: SurveyResponseItem[];
}

export interface FormSubmissionField {
  id: number;
  question: string;
  response: string;
}

export interface FormSubmissionDetail {
  submission: {
    id: number;
    formType: 'Demographics' | 'History' | 'Financing';
    patientId: number;
    date: string | null;
    fieldCount: number;
  };
  signature: { fileName: string } | null;
  fields: FormSubmissionField[];
}

export interface FormListResponse {
  rows: FormListRow[];
  giFormConfigured: boolean;
  giFormUpToDate: boolean;
  currentGiFormId: number | null;
}

export function listPatientForms(patientId: number, includeDeleted = false): Promise<FormListResponse> {
  const params = includeDeleted ? '?includeDeleted=true' : '';
  return apiClient
    .get<ApiResponse<FormListResponse>>(`/patients/${patientId}/forms${params}`)
    .then((r) => r.data.data ?? { rows: [], giFormConfigured: false, giFormUpToDate: false });
}

export function getSurveyResponseDetail(
  patientId: number,
  responseId: number,
  recover = false,
): Promise<SurveyResponseDetail> {
  const params = recover ? '?recover=1' : '';
  return apiClient
    .get<ApiResponse<SurveyResponseDetail>>(
      `/patients/${patientId}/forms/survey-response/${responseId}${params}`,
    )
    .then((r) => r.data.data);
}

/** The completed form's PDF, built on request for the patient chart's response modal. */
export function getSurveyResponsePdf(patientId: number, responseId: number): Promise<CompletedFormPdf> {
  return apiClient
    .get<Blob>(`/patients/${patientId}/forms/survey-response/${responseId}/pdf`, COMPLETED_FORM_PDF_REQUEST)
    .then(toCompletedFormPdf);
}

export function getFormSubmissionDetail(
  patientId: number,
  submissionId: number,
): Promise<FormSubmissionDetail> {
  return apiClient
    .get<ApiResponse<FormSubmissionDetail>>(
      `/patients/${patientId}/forms/submission/${submissionId}`,
    )
    .then((r) => r.data.data);
}

/** Comm-log entry that proves an orphaned form belongs to this patient. */
export interface OrphanFormEvidence {
  commLogId: number;
  subject: string;
  date: string | null;
  preview: string;
}

/**
 * A completed but orphaned (patientID=0) survey response that this patient's own
 * comm log deterministically references (via the embedded surveysResponseID), so
 * it can be attached to the patient after the fact.
 */
export type OrphanMatchType = 'commlog-id' | 'email' | 'phone' | 'identity' | 'deleted';

/** 'orphan' = unattached form matched to the patient; 'deleted' = the patient's own soft-deleted form (can revert). */
export type OrphanCategory = 'orphan' | 'deleted';

export interface OrphanMatchBreakdown {
  email: boolean;
  phone: boolean;
  firstName: boolean;
  lastName: boolean;
  dob: boolean;
}

export interface OrphanFormCandidate {
  surveysResponseId: number;
  surveyTitle: string;
  dateCompleted: string | null;
  answerPreview: string;
  category?: OrphanCategory;
  /** How this form was matched to the patient. 'commlog-id' is deterministic; the rest are identity matches. */
  matchType?: OrphanMatchType;
  /** 999 for a deterministic comm-log-id match; otherwise the count of matching identity fields. */
  matchScore?: number;
  matches?: OrphanMatchBreakdown | null;
  /** When category is 'deleted', the date the form was soft-deleted. */
  deletedDate?: string | null;
  evidence: OrphanFormEvidence | null;
}

export function getOrphanFormCandidates(patientId: number): Promise<OrphanFormCandidate[]> {
  return apiClient
    .get<ApiResponse<OrphanFormCandidate[]>>(`/patients/${patientId}/forms/orphan-candidates`)
    .then((r) => r.data.data ?? []);
}

export interface AttachOrphanResult {
  surveysResponseId: number;
  giBackfilled: boolean;
  /** How many missing answers (e.g. signature, scroll-accept) were recovered from the lead HTML. */
  recoveredAnswers: number;
}

export function attachOrphanForm(
  patientId: number,
  surveysResponseId: number,
): Promise<AttachOrphanResult> {
  return apiClient
    .post<ApiResponse<AttachOrphanResult>>(
      `/patients/${patientId}/forms/attach-orphan`,
      { surveysResponseId },
    )
    .then((r) => r.data.data);
}

export interface AttachLeadFormResult {
  surveysResponseId: number;
  leadId: number;
  giBackfilled: boolean;
  recoveredAnswers: number;
}

/**
 * Put the completed form a lead carries on a patient's chart. The server also
 * marks the lead converted. Refusals come back as `{ error: { code, message } }`
 * with a message written for staff; read it with `getErrorMessage`.
 */
export function attachLeadForm(patientId: number, leadId: number): Promise<AttachLeadFormResult> {
  return apiClient
    .post<ApiResponse<AttachLeadFormResult>>(
      `/patients/${patientId}/forms/attach-lead-form`,
      { leadId },
    )
    .then((r) => r.data.data);
}

export interface MoveSurveyResponseResult {
  responseId: number;
  fromPatientId: number;
  toPatientId: number;
  /** True when the move removed the form's General Intake link from the source patient or added one to the destination. */
  giMoved: boolean;
}

/**
 * Move a completed form from one patient's chart to another. Refusals come back
 * as `{ error: { code, message } }` with a message written for staff; read it
 * with `getErrorMessage` from the api client.
 */
export function moveSurveyResponse(
  fromPatientId: number,
  responseId: number,
  toPatientId: number,
): Promise<MoveSurveyResponseResult> {
  return apiClient
    .post<ApiResponse<MoveSurveyResponseResult>>(
      `/patients/${fromPatientId}/forms/survey-response/${responseId}/move`,
      { toPatientId },
    )
    .then((r) => r.data.data);
}
